/**
 * ============================================================================
 * SCREEN: Home (Root Redirect)
 * ============================================================================
 * @description Landing page that redirects authenticated users to dashboard or unauthenticated users to login
 *
 * ROUTING:
 * - Frontend URL: /
 * - Layout: None (standalone page)
 *
 * API CONNECTIONS:
 * - No API calls (client-side routing only)
 *
 * KEY FEATURES:
 * - Automatic authentication check and redirect
 * - Loading state with logo and spinner
 * - Multi-language support with LanguageAwareLogo
 *
 * DATA STRUCTURES:
 * - Uses useAuth hook for authentication state
 *
 * VALIDATION RULES:
 * - None (routing only)
 *
 * STATE MANAGEMENT:
 * - isAuthenticated: boolean from useAuth
 * - isLoading: boolean from useAuth
 *
 * ACCESS CONTROL:
 * - Public route (redirects based on auth status)
 */
'use client';


import { useEffect } from 'react';
import { useAuth } from '@/hooks/auth/useAuth';
import { useRouter } from 'next/navigation';
import { Loader2 } from '@/components/ui/icons';
import { useTranslation } from '@/hooks/useModuleTranslation';
import { LanguageAwareLogo } from '@/components/brand/LanguageAwareLogo';

export default function HomePage() {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const translation = useTranslation('home');
  const t = translation.t;

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        router.push('/dashboard');
      } else {
        router.push('/login');
      }
    }
  }, [isAuthenticated, isLoading, router]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <LanguageAwareLogo
              size="md"
              showText={false}
              className="justify-center"
            />
          </div>
          <h1 className="title-glow mb-2">
            {t('title')}
          </h1>
          <div className="flex items-center justify-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-gray-600 dark:text-gray-300">
              {t('messages.loading')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
